/**
 * Unsaved-changes navigation guard.
 *
 * Blocks SPA navigation when an active editorial session has
 * pending operations or when the page has an element flagged
 * with the `sdx-editing` class (indicating a live inline edit
 * or a dirty Drupal form being watched by a theme-side host).
 *
 * The user-facing confirmation calls `Drupal.sdx.confirm()` when
 * the sdx/dialog library is loaded; that primitive returns a
 * promise the router awaits before dispatching the navigation.
 * When the dialog library is absent the guard falls back to the
 * native `window.confirm()` so the UX still degrades gracefully.
 */

interface SdxConfirmApi {
  confirm?: (options: {
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    size?: 'xs' | 's' | 'm' | 'l' | 'xl';
  }) => Promise<boolean>;
}

export function hasUnsavedChanges(): boolean {
  const session = drupalSettings.sdxEditSession as
    | { active?: boolean; op_count?: number }
    | undefined;
  if (session?.active && typeof session.op_count === 'number' && session.op_count > 0) {
    return true;
  }
  return !!document.querySelector('.sdx-editing');
}

/**
 * Asynchronously confirms a navigation when the page carries
 * unsaved changes. Returns a promise that resolves `true` when
 * navigation should proceed and `false` when the user cancels.
 *
 * Always resolves `true` when there's nothing unsaved, so the
 * caller can use it unconditionally:
 *
 *   if (await confirmNavigation()) {
 *     controller.start(href);
 *   }
 */
export async function confirmNavigation(): Promise<boolean> {
  if (!hasUnsavedChanges()) return true;

  const sdx = (window as unknown as { Drupal?: { sdx?: SdxConfirmApi } }).Drupal?.sdx;
  if (sdx && typeof sdx.confirm === 'function') {
    return sdx.confirm({
      title: 'Leave this page?',
      message: 'You have unsaved changes. If you leave now, your changes will be lost.',
      confirmLabel: 'Leave',
      cancelLabel: 'Stay',
      destructive: true,
      size: 's',
    });
  }

  // Fallback for environments without sdx/dialog loaded. Not ideal
  // visually but keeps the guard functional on non-SDX themes.
  return window.confirm('You have unsaved changes. Leave this page?');
}
